// Helpers for the oidc-client signin state that lives in localStorage under the
// "oidc." prefix. signinRedirect() writes one entry per login attempt and the
// callback reads it back by the state parameter Keycloak returns. When that entry
// is missing the login cannot complete on its own - the only way out is to drop
// the orphaned entries and start a clean login.

const STATE_PREFIX = "oidc.";
// The signed in user is stored under "oidc.user:<authority>:<client>". It shares
// the prefix but must survive a purge, so it is matched separately.
const USER_PREFIX = "oidc.user:";
const RETRY_KEY = "up.signin_retry";

// Thrown by oidc-client when the state in the signin response cannot be matched
// against anything this browser stored: the login was started somewhere else (a
// shared or bookmarked Keycloak url, another browser profile, a device whose site
// data was wiped), or the callback url was re-entered after the state was consumed.
const RECOVERABLE_ERRORS = [
    "No matching state found in storage",
    "No state in response"
];

// Routes that are in the middle of an oidc redirect. The state entry they are
// about to read is not stale, it is the one being used, so cleanup stays away.
const CALLBACK_PATHS = [
    "/signin-oidc",
    "/silentrenew",
    "/silent_renew.html",
    "/logout/callback"
];

export function isRecoverableSigninError(error) {
    const message = !!error && !!error.message ? error.message : String(error || "");
    return RECOVERABLE_ERRORS.some(known => message.indexOf(known) !== -1);
}

export function isOidcCallbackPath(pathname) {
    return CALLBACK_PATHS.indexOf(pathname) !== -1;
}

// Drops every orphaned oidc.<state> entry, keeping the signed in user.
// Returns the number of entries removed.
export function purgeOidcState() {
    try {
        const stale = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!!key && key.indexOf(STATE_PREFIX) === 0 && key.indexOf(USER_PREFIX) !== 0) {
                stale.push(key);
            }
        }
        stale.forEach(key => localStorage.removeItem(key));
        return stale.length;
    } catch (error) {
        // Storage blocked by browser policy - there is nothing to clean up.
        return 0;
    }
}

// The retry marker lives in sessionStorage so it survives the trip to Keycloak
// and back but not a new tab. A browser that will not let us remember the attempt
// reports "already retried", which costs one error page instead of risking an
// endless redirect loop.
export function hasRetriedSignin() {
    try {
        return sessionStorage.getItem(RETRY_KEY) === "1";
    } catch (error) {
        return true;
    }
}

export function markSigninRetried() {
    try {
        sessionStorage.setItem(RETRY_KEY, "1");
    } catch (error) {
        // Storage blocked - hasRetriedSignin() already errs on the safe side.
    }
}

export function clearSigninRetry() {
    try {
        sessionStorage.removeItem(RETRY_KEY);
    } catch (error) {
        // Storage blocked - nothing was written in the first place.
    }
}

// Fire and forget cleanup of state entries older than staleStateAge. Called at
// startup: entries used to pile up on every abandoned or failed login because
// nothing ever removed them.
export function clearStaleStateSafely(userManager) {
    if (isOidcCallbackPath(window.location.pathname)) {
        return;
    }

    try {
        const pending = userManager.clearStaleState();
        if (!!pending && typeof pending.catch === "function") {
            pending.catch(() => { });
        }
    } catch (error) {
        // Storage blocked by browser policy - there is nothing to clean up.
    }
}
